import { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { CalendarClock, CheckCircle2, Clock, Mail, MapPin, Phone } from "lucide-react";
import { toast } from "sonner";
import { telHref } from "@/lib/scroll";
import { useSettings } from "@/lib/SettingsContext";

const EASE = [0.16, 1, 0.3, 1];
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICES = [
  "Regular Domestic Clean",
  "Deep Clean",
  "End of Tenancy Clean",
  "Oven Detailing",
  "Commercial & Office",
];

const inputCls =
  "w-full rounded-xl border border-[#E5DEC3] bg-white px-4 py-3.5 text-sm text-[#121A16] placeholder-[#4A5A52]/50 outline-none transition-[border-color,box-shadow] duration-300 focus:border-[#0F382A] focus:shadow-[0_0_0_3px_rgba(15,56,42,0.08)]";

const labelCls = "mb-2 block font-mono text-[11px] uppercase tracking-[0.2em] text-[#4A5A52]";

const ContactRow = ({ icon: Icon, label, value, href, testid }) => {
  const inner = (
    <>
      <span className="flex h-11 w-11 items-center justify-center rounded-full border border-[#E5DEC3] bg-white transition-colors duration-300 group-hover:border-[#D4AF37]">
        <Icon className="h-4 w-4 text-[#0F382A]" strokeWidth={1.75} />
      </span>
      <span>
        <span className="block font-mono text-[10px] uppercase tracking-[0.2em] text-[#4A5A52]">{label}</span>
        <span className="text-base font-semibold text-[#0F382A]">{value}</span>
      </span>
    </>
  );
  return href ? (
    <a data-testid={testid} href={href} className="group flex items-center gap-4">{inner}</a>
  ) : (
    <div data-testid={testid} className="flex items-center gap-4">{inner}</div>
  );
};

export default function BookingForm({ prefill }) {
  const settings = useSettings();
  const empty = { name: "", phone: "", email: "", service: SERVICES[0], date: "", slot: "", message: "" };
  const [form, setForm] = useState(empty);
  const [sending, setSending] = useState(false);
  const [availability, setAvailability] = useState(null);
  const [confirmation, setConfirmation] = useState(null);

  const loadAvailability = () =>
    axios.get(`${API}/availability`).then((r) => setAvailability(r.data)).catch(() => {});

  useEffect(() => {
    loadAvailability();
  }, []);

  useEffect(() => {
    if (prefill) {
      setConfirmation(null);
      setForm((f) => ({
        ...f,
        service: prefill.service,
        message: `My estimate: ${prefill.estimate}`,
      }));
    }
  }, [prefill]);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));
  const setDate = (e) => setForm((f) => ({ ...f, date: e.target.value, slot: "" }));

  const dayBusy = form.date && availability?.busy_days?.includes(form.date);
  const takenSlots = (form.date && availability?.taken?.[form.date]) || [];
  const tomorrow = new Date(Date.now() + 86400000).toISOString().split("T")[0];
  const slotLabel = (id) => availability?.slots?.find((s) => s.id === id)?.label || id;

  const submit = async (e) => {
    e.preventDefault();
    if (form.date && !form.slot) {
      toast.error("Please choose a time slot for your preferred date.");
      return;
    }
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/enquiries`, {
        name: form.name,
        phone: form.phone,
        email: form.email,
        service: form.service,
        preferred_date: form.date || null,
        preferred_slot: form.slot || null,
        message: form.message || null,
        estimate: prefill?.estimate || null,
      });
      toast.success("Booked in — we'll call to confirm your slot within one working hour.");
      setConfirmation({
        name: data.name,
        service: data.service,
        date: data.preferred_date,
        slot: data.preferred_slot ? slotLabel(data.preferred_slot) : null,
        cancelUrl: `${window.location.origin}/cancel?token=${data.cancel_token}`,
      });
      setForm(empty);
      loadAvailability();
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (err.response?.status === 409 && typeof detail === "string") {
        toast.error(detail);
        loadAvailability();
      } else {
        toast.error(`Something went wrong — please call us on ${settings.phone_display}.`);
      }
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="booking" className="py-24 lg:py-36">
      <div className="mx-auto grid max-w-7xl grid-cols-1 gap-16 px-6 lg:grid-cols-12 lg:px-10">
        <motion.div
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, ease: EASE }}
          className="lg:col-span-5"
        >
          <p className="mb-4 font-mono text-xs uppercase tracking-[0.25em] text-[#1B523F]">
            Book Your Clean
          </p>
          <h2 className="font-serif text-4xl font-light leading-[1.05] tracking-tight text-[#121A16] sm:text-5xl lg:text-6xl">
            Let's make it
            <br />
            <em className="italic text-[#0F382A]">sparkle.</em>
          </h2>
          <p className="mt-6 max-w-sm text-base leading-relaxed text-[#4A5A52]">
            Pick your exact date and time slot — busy days are already blocked
            out. We'll call to confirm within one working hour.
          </p>

          <div className="mt-10 space-y-5">
            <ContactRow testid="contact-phone" icon={Phone} label="Call Don directly" value={settings.phone_display} href={telHref(settings.phone_display)} />
            <ContactRow testid="contact-email" icon={Mail} label="Email" value={settings.email} href={`mailto:${settings.email}`} />
            <ContactRow testid="contact-address" icon={MapPin} label="Based at" value={settings.address} />
            <ContactRow testid="contact-hours" icon={Clock} label="Hours" value={settings.hours} />
          </div>

          <div
            data-testid="contact-map"
            className="mt-8 overflow-hidden rounded-[1.5rem] border border-[#E5DEC3] shadow-[0_20px_40px_-24px_rgba(11,29,22,0.2)]"
          >
            <iframe
              title="Don Sparkle Cleaning — Quarry Spring, Harlow CM20 3HS"
              src={`https://maps.google.com/maps?q=${encodeURIComponent(settings.address || "Harlow CM20 3HS")}&z=15&output=embed`}
              className="h-64 w-full"
              loading="lazy"
            />
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, delay: 0.12, ease: EASE }}
          className="lg:col-span-7"
        >
          {confirmation ? (
            <div
              data-testid="booking-confirmation"
              className="rounded-[2rem] border border-[#D4AF37]/50 bg-white p-8 shadow-[0_40px_80px_-40px_rgba(11,29,22,0.18)] lg:p-12"
            >
              <CheckCircle2 className="h-12 w-12 text-[#0F382A]" strokeWidth={1.5} />
              <p className="mt-5 font-serif text-3xl font-light text-[#121A16]">
                Thank you, {confirmation.name.split(" ")[0]} — you're booked in.
              </p>
              <div className="mt-6 space-y-2 rounded-xl bg-[#FAF8F5] p-5 text-sm text-[#4A5A52]">
                <p><span className="font-semibold text-[#0F382A]">Service:</span> {confirmation.service}</p>
                {confirmation.date && (
                  <p><span className="font-semibold text-[#0F382A]">Date:</span> {confirmation.date}</p>
                )}
                {confirmation.slot && (
                  <p><span className="font-semibold text-[#0F382A]">Time:</span> {confirmation.slot}</p>
                )}
              </div>
              <p className="mt-5 text-sm leading-relaxed text-[#4A5A52]">
                A confirmation email is on its way to you with a cancellation
                link. Keep it safe — if plans change, one tap frees up your
                slot automatically. Your link:
              </p>
              <a
                data-testid="booking-cancel-link"
                href={confirmation.cancelUrl}
                className="mt-3 block break-all rounded-xl border border-[#E5DEC3] bg-[#FAF8F5] px-4 py-3 font-mono text-xs text-[#0F382A] underline decoration-[#D4AF37] underline-offset-4"
              >
                {confirmation.cancelUrl}
              </a>
              <button
                data-testid="booking-again-button"
                onClick={() => setConfirmation(null)}
                className="mt-8 rounded-full border border-[#0F382A] px-8 py-3 text-sm font-semibold text-[#0F382A] transition-[background-color,color] duration-300 hover:bg-[#0F382A] hover:text-[#FAF8F5]"
              >
                Make another booking
              </button>
            </div>
          ) : (
            <form
              data-testid="booking-form"
              onSubmit={submit}
              className="rounded-[2rem] border border-[#E5DEC3] bg-white p-8 shadow-[0_40px_80px_-40px_rgba(11,29,22,0.18)] lg:p-12"
            >
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                <div>
                  <label className={labelCls} htmlFor="booking-name">Name</label>
                  <input id="booking-name" data-testid="booking-name-input" required value={form.name} onChange={set("name")} placeholder="Your full name" className={inputCls} />
                </div>
                <div>
                  <label className={labelCls} htmlFor="booking-phone">Phone</label>
                  <input id="booking-phone" data-testid="booking-phone-input" required type="tel" value={form.phone} onChange={set("phone")} placeholder="07xxx xxxxxx" className={inputCls} />
                </div>
                <div>
                  <label className={labelCls} htmlFor="booking-email">Email</label>
                  <input id="booking-email" data-testid="booking-email-input" required type="email" value={form.email} onChange={set("email")} placeholder="you@example.com" className={inputCls} />
                </div>
                <div>
                  <label className={labelCls} htmlFor="booking-service">Service</label>
                  <select id="booking-service" data-testid="booking-service-select" value={form.service} onChange={set("service")} className={inputCls}>
                    {SERVICES.map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                </div>
                <div className="sm:col-span-2">
                  <label className={labelCls} htmlFor="booking-date">Preferred date</label>
                  <input
                    id="booking-date"
                    data-testid="booking-date-input"
                    type="date"
                    min={tomorrow}
                    value={form.date}
                    onChange={setDate}
                    className={inputCls}
                  />
                </div>

                {form.date && (
                  <div className="sm:col-span-2">
                    <label className={labelCls}>
                      <CalendarClock className="mr-1.5 inline h-3.5 w-3.5 text-[#D4AF37]" strokeWidth={2} />
                      Time slot
                    </label>
                    {dayBusy ? (
                      <p data-testid="booking-day-busy" className="rounded-xl border border-[#D4AF37]/50 bg-[#F4ECE1] px-4 py-3 text-sm text-[#8a6d1a]">
                        This day is fully booked — please pick another date.
                      </p>
                    ) : (
                      <div className="flex flex-wrap gap-3">
                        {(availability?.slots || []).map((s) => {
                          const taken = takenSlots.includes(s.id);
                          const active = form.slot === s.id;
                          return (
                            <button
                              key={s.id}
                              type="button"
                              data-testid={`booking-slot-${s.id}`}
                              disabled={taken}
                              onClick={() => setForm((f) => ({ ...f, slot: s.id }))}
                              className={`rounded-full border px-5 py-2.5 text-sm font-medium transition-[background-color,color,border-color,opacity] duration-300 ${
                                taken
                                  ? "cursor-not-allowed border-[#E5DEC3] bg-[#F3EFEA] text-[#4A5A52]/40 line-through"
                                  : active
                                    ? "border-[#D4AF37] bg-[#D4AF37] text-[#0B1D16]"
                                    : "border-[#E5DEC3] bg-white text-[#4A5A52] hover:border-[#D4AF37]/60 hover:text-[#0B1D16]"
                              }`}
                            >
                              {s.label}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}

                <div className="sm:col-span-2">
                  <label className={labelCls} htmlFor="booking-message">Special instructions</label>
                  <textarea id="booking-message" data-testid="booking-message-input" rows={4} value={form.message} onChange={set("message")} placeholder="Access details, pets, priorities…" className={`${inputCls} resize-none`} />
                </div>
              </div>
              <button
                data-testid="booking-submit-button"
                type="submit"
                disabled={sending}
                className="mt-8 w-full rounded-full bg-[#0F382A] py-4 text-sm font-semibold text-[#FAF8F5] transition-[background-color,transform,opacity] duration-300 hover:-translate-y-0.5 hover:bg-[#1B523F] disabled:opacity-60 sm:w-auto sm:px-12"
              >
                {sending ? "Sending…" : "Send enquiry"}
              </button>
            </form>
          )}
        </motion.div>
      </div>
    </section>
  );
}
